import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { api } from "../../api/client";
import { AlertBanner } from "../../components/feedback/AlertBanner";
import { TurnstileWidget } from "../../components/auth/TurnstileWidget";
import { AuthLayout } from '../../components/layout/AuthLayout';
import { Field } from '../../components/forms/Field';
import { apiError } from "../../lib/apiError";
import { CURRENT_TERMS_VERSION } from "../../lib/terms";

export function RegisterPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [google, setGoogle] = useState(false);
  const [turnstileEnabled, setTurnstileEnabled] = useState(false);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await api.GET("/api/auth/config");
      if (cancelled || result.error) {
        return;
      }
      const data = result.data as {
        google_oauth_enabled?: boolean;
        turnstile_enabled?: boolean;
        turnstile_site_key?: string;
      };
      setGoogle(Boolean(data.google_oauth_enabled));
      setTurnstileEnabled(Boolean(data.turnstile_enabled));
      setTurnstileSiteKey(data.turnstile_site_key || "");
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!acceptTerms) {
      setError("Zaakceptuj regulamin i politykę prywatności, aby się zarejestrować.");
      return;
    }
    setBusy(true);
    setError(null);
    const result = await api.POST("/api/auth/register", {
      body: {
        email,
        password,
        password_confirm: passwordConfirm,
        captcha_token: captchaToken,
        accept_terms: acceptTerms,
        terms_version: CURRENT_TERMS_VERSION,
      },
    });
    setBusy(false);
    if (result.error) {
      setError(apiError(result.error, "Nie udało się zarejestrować."));
      return;
    }
    const data = result.data as { email?: string };
    const saved = data.email || email.trim().toLowerCase();
    navigate(`/register-pending?email=${encodeURIComponent(saved)}`, {
      replace: true,
    });
  }

  return (
    <AuthLayout title="Utwórz konto">
      <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />
      <p className="auth__intro">
        Aplikacja jest w fazie <strong>beta</strong> — pełny dostęp za darmo. Planowane płatne
        plany zostaną ogłoszone z 30-dniowym wyprzedzeniem.
      </p>
      {google ? (
        <>
          <Link className="btn btn--google" to="/google-consent">
            Kontynuuj z Google
          </Link>
          <p className="auth__divider">lub e-mailem</p>
        </>
      ) : null}
      <form
        className="auth__form"
        data-testid="register-form"
        autoComplete="off"
        onSubmit={onSubmit}
      >
        <Field label="Adres e-mail">
          <input
            id="email"
            className="input"
            type="email"
            name="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            autoFocus
            autoComplete="email"
            data-testid="register-email"
          />
        </Field>
        <Field label="Hasło">
          <input
            id="password"
            className="input"
            type="password"
            name="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
            data-testid="register-password"
          />
          <span className="field__hint">Minimum 8 znaków</span>
        </Field>
        <Field label="Powtórz hasło">
          <input
            id="password_confirm"
            className="input"
            type="password"
            name="password_confirm"
            value={passwordConfirm}
            onChange={(event) => setPasswordConfirm(event.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
            data-testid="register-password-confirm"
          />
        </Field>
        <label className="auth__checkbox">
          <input
            type="checkbox"
            checked={acceptTerms}
            onChange={(event) => setAcceptTerms(event.target.checked)}
            data-testid="register-accept-terms"
          />
          <span>
            Akceptuję{" "}
            <Link to="/terms" target="_blank" rel="noopener noreferrer">
              regulamin
            </Link>{" "}
            (wersja {CURRENT_TERMS_VERSION}) oraz{" "}
            <Link to="/privacy" target="_blank" rel="noopener noreferrer">
              politykę prywatności
            </Link>
            .
          </span>
        </label>
        {turnstileEnabled && turnstileSiteKey ? (
          <TurnstileWidget
            siteKey={turnstileSiteKey}
            onToken={setCaptchaToken}
            onExpire={() => setCaptchaToken("")}
          />
        ) : null}
        <button
          className="btn btn--block"
          type="submit"
          disabled={busy || !acceptTerms || (turnstileEnabled && !captchaToken)}
          data-testid="register-submit"
        >
          Zarejestruj się
        </button>
      </form>
      <p className="auth__footer">
        Masz już konto? <Link to="/login">Zaloguj się</Link>
        <br />
        <Link to="/welcome">Strona główna</Link>
      </p>
    </AuthLayout>
  );
}
